// src/components/common/CustomDataGridMobile.tsx - WITH EXPAND SUPPORT
import React, { useState, useRef, useLayoutEffect } from 'react';
import { useListContext, DatagridProps, RaRecord, Identifier, RecordContextProvider } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
  Grid,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Groups as GroupsIcon,
  QrCode2 as QrCode2Icon,
  Place as PlaceIcon,
  Event as EventIcon,
  Sell as SellIcon,
} from '@mui/icons-material';

interface CustomDataGridMobileProps<RecordType extends RaRecord = any>
  extends Omit<DatagridProps<RecordType>, 'children'> {
  children: React.ReactNode;
  /** Field used as big card title (e.g., "name"). */
  primarySource?: string;
  /** Field used as subtitle under the title (e.g., "groupName"). */
  secondarySource?: string;
  /** Number of columns for details on >= sm screens. */
  detailColumns?: 2 | 3;
}

/** Avoid SVG clipping in tight line-heights */
const IconWrap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      lineHeight: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 18,
      width: 18,
      overflow: 'visible',
    }}
  >
    {children}
  </Box>
);

/**
 * Renders `children` as-is, but falls back to an em dash when the field's own
 * render produced no visible text (e.g. a source that is empty for this record) —
 * a blank cell reads as broken, "—" reads as "checked, nothing there".
 */
const EmptyableValue: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  useLayoutEffect(() => {
    setIsEmpty(!ref.current || ref.current.textContent?.trim() === '');
  });

  return (
    <>
      <Box component="span" ref={ref} sx={{ display: isEmpty ? 'none' : 'contents' }}>
        {children}
      </Box>
      {isEmpty && (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          —
        </Typography>
      )}
    </>
  );
};

/** Small, subtle icon by source */
const getIconForSource = (source?: string) => {
  if (!source) return null;
  const s = source.toLowerCase();
  const iconSx = { fontSize: 18, opacity: 0.75, overflow: 'visible' as const };
  if (s.includes('group')) return <IconWrap><GroupsIcon sx={iconSx} /></IconWrap>;
  if (s.includes('install') || s.includes('code')) return <IconWrap><QrCode2Icon sx={iconSx} /></IconWrap>;
  if (s.includes('address') || s.includes('dir')) return <IconWrap><PlaceIcon sx={iconSx} /></IconWrap>;
  if (s.includes('created') || s.includes('date')) return <IconWrap><EventIcon sx={iconSx} /></IconWrap>;
  if (s.includes('tag')) return <IconWrap><SellIcon sx={iconSx} /></IconWrap>;
  return null;
};

/**
 * ExpandableCard - Individual card with expand functionality
 */
const ExpandableCard = <RecordType extends RaRecord = any>({
  record,
  index,
  children,
  controls,
  actions,
  titleEl,
  subtitleEl,
  others,
  detailColumns,
  ExpandComponent,
  theme,
  isXs,
}: {
  record: RecordType;
  index: number;
  children: React.ReactNode;
  controls: React.ReactElement[];
  actions: React.ReactElement[];
  titleEl: React.ReactElement | null;
  subtitleEl: React.ReactElement | null;
  others: React.ReactElement[];
  detailColumns: 2 | 3;
  ExpandComponent?: any;  // Simplified type - accepts whatever React Admin passes
  theme: any;
  isXs: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    if (ExpandComponent) {
      setExpanded(!expanded);
    }
  };

  const isExpandable = !!ExpandComponent;

  return (
    <Card
      key={(record as any).id ?? index}
      onClick={isExpandable ? handleExpandClick : undefined}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: 0,
        overflow: 'hidden',
        transition: 'box-shadow .2s ease, border-color .2s ease, transform .1s ease',
        cursor: isExpandable ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: 2,
          borderColor: theme.palette.primary.light,
          ...(isExpandable && {
            transform: 'translateY(-2px)',
          }),
        },
        ...(expanded && {
          borderColor: theme.palette.primary.main,
          boxShadow: 3,
        }),
      }}
    >
      <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
        {/* Header: selection control(s) + title + expand icon */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          {controls.length > 0 && (
            <Box sx={{ display: 'flex', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
              {controls.map((c, i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44 }}
                >
                  {React.cloneElement(c as any, { record })}
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Title */}
            <Box sx={{ '& .MuiTypography-root': { fontSize: { xs: '1.06rem', sm: '1.12rem' } } }}>
              {titleEl
                ? React.cloneElement(titleEl as any, {
                    record,
                    sx: { fontWeight: 700, color: 'text.primary', lineHeight: 1.3 },
                  })
                : null}
            </Box>
            {/* Subtitle */}
            {subtitleEl && (
              <Box
                sx={{
                  mt: 0.35,
                  '& .MuiTypography-root': { fontSize: { xs: '.92rem', sm: '.96rem' }, color: 'text.secondary' },
                }}
              >
                {React.cloneElement(subtitleEl as any, { record })}
              </Box>
            )}
          </Box>

          {/* Expand icon (right) or Actions */}
          {isExpandable ? (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleExpandClick();
              }}
              aria-expanded={expanded}
              aria-label="show more"
              size="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                color: expanded ? 'primary.main' : 'action.active',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          ) : (
            actions.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {actions.map((a, i) =>
                  React.cloneElement(a as any, {
                    key: i,
                    record,
                    size: isXs ? 'small' : 'medium',
                  })
                )}
              </Box>
            )
          )}
        </Box>

        {/* Header divider */}
        <Divider sx={{ my: { xs: 0.9, sm: 1.1 }, opacity: 0.55 }} />

        {/* Details grid */}
        <Grid container spacing={{ xs: 1, sm: 1.5 }} columns={{ xs: 1, sm: detailColumns }}>
          {others.map((field, i) => {
            const label = field.props?.label ?? field.props?.source;
            const source = field.props?.source as string | undefined;
            const isMono =
              ['installationcode', 'createdat', 'created', 'code'].includes(
                (source ?? '').toLowerCase()
              );

            return (
              <React.Fragment key={label ?? i}>
                <Grid item xs={1} sm={1}>
                  {/* Label + value in a single flexible row that wraps if needed */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      columnGap: 0.8,
                      rowGap: 0.25,
                      minHeight: 30,
                      py: 0.25,
                    }}
                  >
                    {/* Label (with icon) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      {getIconForSource(source)}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          // Teal marks one thing at a time (rule 1) — a field
                          // label repeated 4-6x per row is not it (F-B-003).
                          color: theme.custom.text.label,
                          lineHeight: 1.35,
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>

                    {/* Value */}
                    <Box
                      sx={{
                        flex: '1 1 0px',
                        minWidth: 0,
                        '& .MuiTypography-root': {
                          fontSize: theme.typography.body2.fontSize,
                          fontWeight: 500,
                          color: 'text.primary',
                          lineHeight: 1.45,
                        },
                        '& .MuiChip-root': {
                          height: 24,
                          fontSize: theme.typography.body2.fontSize as any,
                          fontWeight: 500,
                        },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontFamily: isMono
                          ? "ui-monospace, SFMono-Regular, Menlo, monospace"
                          : theme.typography.fontFamily,
                      }}
                    >
                      <EmptyableValue>{React.cloneElement(field as any, { record })}</EmptyableValue>
                    </Box>
                  </Box>
                </Grid>

                {/* Subtle divider every 3 fields */}
                {(i % 3 === 2 || i === others.length - 1) && i !== others.length - 1 && (
                  <Grid item xs={1} sm={detailColumns}>
                    <Divider sx={{ mt: 0.5, opacity: 0.08 }} />
                  </Grid>
                )}
              </React.Fragment>
            );
          })}
        </Grid>

        {/* Expanded content */}
        {ExpandComponent && (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <RecordContextProvider value={record}>
                {typeof ExpandComponent === 'function' ? (
                  <ExpandComponent
                    id={(record as any).id}
                    record={record}
                    resource=""
                  />
                ) : (
                  ExpandComponent
                )}
              </RecordContextProvider>
            </Box>
          </Collapse>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * CustomDataGridMobile - mobile-first card list with responsive layout and EXPAND support
 */
const CustomDataGridMobile = <RecordType extends RaRecord = any>({
  children,
  primarySource,
  secondarySource,
  detailColumns = 2,
  expand,
}: CustomDataGridMobileProps<RecordType>) => {
  const { data = [], isLoading } = useListContext();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));

  /** Split children into title, subtitle, detail fields and actions */
  const splitChildren = (record: RecordType) => {
    const fields: Array<React.ReactElement & { props: any }> = [];
    const actions: Array<React.ReactElement & { props: any }> = [];
    const controls: Array<React.ReactElement & { props: any }> = [];

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return;
      const c = child as React.ReactElement & { props: any };
      const { source, label } = c.props || {};
      // A bulk-selection or row-expand column has no `source` (nothing to display)
      // and its own `label` is a rendered control (a checkbox), not translated
      // text — it must never be treated as a data field (F-B "S10 mobile card").
      if (!source && React.isValidElement(label)) controls.push(c);
      else if (!source && !label) actions.push(c);
      else fields.push(c);
    });

    let titleEl: React.ReactElement | null = null;
    let subtitleEl: React.ReactElement | null = null;
    const others: React.ReactElement[] = [];

    for (const f of fields) {
      const src = f.props?.source;
      if (!titleEl && primarySource && src === primarySource) {
        titleEl = f;
        continue;
      }
      if (!subtitleEl && secondarySource && src === secondarySource) {
        subtitleEl = f;
        continue;
      }
      others.push(f);
    }

    // Fallback: if no explicit primarySource, take the first field as title
    if (!titleEl && fields.length) {
      titleEl = fields[0];
      const idx = others.indexOf(fields[0]);
      if (idx >= 0) others.splice(idx, 1);
    }

    return { titleEl, subtitleEl, others, actions, controls };
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
        <Stack spacing={1.5}>
          {[1, 2, 3].map((i) => (
            <Card key={i} sx={{ mx: 'auto', width: '100%', maxWidth: 920 }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Skeleton variant="text" width="50%" height={30} />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="rectangular" height={56} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  // Empty state
  if (!data.length) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
        <Card sx={{ mx: 'auto', width: '100%', maxWidth: 920 }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="body1" color="text.secondary">
              No hay datos disponibles
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 0.5, sm: 2 }, py: 2, mx: 'auto', maxWidth: 920, overflow: 'hidden' }}>
      <Stack spacing={{ xs: 1, sm: 1.5 }}>
        {data.map((record: RecordType, idx) => {
          const { titleEl, subtitleEl, others, actions, controls } = splitChildren(record);

          return (
            <ExpandableCard
              key={(record as any).id ?? idx}
              record={record}
              index={idx}
              titleEl={titleEl}
              subtitleEl={subtitleEl}
              others={others}
              actions={actions}
              controls={controls}
              detailColumns={detailColumns}
              ExpandComponent={expand}
              theme={theme}
              isXs={isXs}
            >
              {children}
            </ExpandableCard>
          );
        })}
      </Stack>
    </Box>
  );
};

export default CustomDataGridMobile;